import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { TokenStorage } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(TokenStorage.getAccessToken());

  // Initialize auth on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = TokenStorage.getAccessToken();
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          setAccessToken(token);
        } catch (error) {
          TokenStorage.clearTokens();
          setUser(null);
          setAccessToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = useCallback(async (email, password, remember = false) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    TokenStorage.setTokens(access_token, refresh_token, remember);
    setAccessToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (data) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, data);
    const { access_token, refresh_token, user: userData } = response.data;
    TokenStorage.setTokens(access_token, refresh_token);
    setAccessToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    TokenStorage.clearTokens();
    setAccessToken(null);
    setUser(null);
  }, []);

  const isWorker = user?.role === 'WORKER' || user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      accessToken,
      login,
      register,
      logout,
      isWorker,
      isAdmin,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};
